// src/pages/CheckoutPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CheckCircle, XCircle, ArrowLeft, Lock, Tag, X, ShieldCheck, Zap, RotateCcw, Check, Mail, RefreshCw } from 'lucide-react';
import { auth } from '../firebase';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import stripeService from '../services/stripeService';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { sendVerificationEmail } from '../utils/authUtils';
import { useRegionalPricing } from '../hooks/useRegionalPricing';
import { isPurchasablePlan, PLAN_DURATION_DAYS, PLAN_NAME_KEYS } from '../config/plans';
import {
  trackCheckoutStart,
  trackVerificationGateShown,
  trackVerificationGateResolved,
  trackPaymentIntentCreated,
  trackPurchase,
} from '../utils/conversionEvents';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

/* ─── Checkout Form ─────────────────────────────────────────── */
const CheckoutForm = ({ planId, amount, clientSecret, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setIsProcessing(true);
    setError(null);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) { setError(submitError.message); setIsProcessing(false); return; }
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: `${window.location.origin}/checkout/success?planId=${planId}` },
        redirect: 'if_required',
      });
      if (confirmError) { setError(confirmError.message); setIsProcessing(false); }
      else onSuccess();
    } catch (err) {
      setError(err.message || t('checkout.error.paymentFailed'));
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  if (!stripe || !elements) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <LogamiLogoSpinner size="lg" decorative={false} alt="Loading" />
        <p className="text-sm text-black/50 font-medium">{t('checkout.loading')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border-2 border-red-400 rounded-2xl p-4">
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 font-bold">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-black text-white py-4 px-6 rounded-2xl font-black text-base flex items-center justify-center gap-2.5 border-[3px] border-black transition-all duration-300 hover:bg-[#3A86FF] hover:border-[#3A86FF] hover:shadow-[6px_6px_0px_0px_rgba(58,134,255,0.3)] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isProcessing
          ? <><LogamiLogoSpinner size="sm" decorative={false} alt="Processing" /><span>{t('checkout.processing')}</span></>
          : <><Lock className="w-4 h-4" /><span>Pay {stripeService.formatAmount(amount)}. Secure</span></>
        }
      </button>

      <div className="flex items-center justify-center gap-4">
        <span className="flex items-center gap-1.5 text-xs text-black/40 font-bold">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />256-bit SSL
        </span>
        <span className="w-px h-3 bg-black/10" />
        <span className="flex items-center gap-1.5 text-xs text-black/40 font-bold">
          <Lock className="w-3 h-3" />PCI Compliant
        </span>
        <span className="w-px h-3 bg-black/10" />
        <span className="text-xs text-black/40 font-bold">Stripe</span>
      </div>
    </form>
  );
};

/* ─── Main Checkout Page ─────────────────────────────────────── */
const CHECKOUT_FROM_ONBOARDING_KEY = 'logami_checkout_from_onboarding';

const FEATURES = [
  'Access to all mock exams for TELC, Goethe & ÖSD',
  'AI writing feedback on every essay',
  'Full grammar & vocabulary exercises',
  'Section-by-section exam lessons',
  'Personalized study plan & progress tracking',
];

const CheckoutPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [isRefreshingVerification, setIsRefreshingVerification] = useState(false);
  const [verificationRefreshNonce, setVerificationRefreshNonce] = useState(0);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [redirectError, setRedirectError] = useState(null);

  const [discountCode, setDiscountCode] = useState('');
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [discountError, setDiscountError] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [finalAmount, setFinalAmount] = useState(null);
  const [originalAmount, setOriginalAmount] = useState(null);

  // Old links and abandoned-checkout reminders can still carry a retired plan
  // (e.g. planId=monthly). The server refuses those, so fall back to 3 months.
  const requestedPlanId = searchParams.get('planId');
  const planId = isPurchasablePlan(requestedPlanId) ? requestedPlanId : 'quarterly';
  const planName = (planId === requestedPlanId && searchParams.get('planName')) || t(PLAN_NAME_KEYS[planId]);
  // The price is never read from the URL — it comes from the plan table, and the
  // authoritative value is whatever the server returns from createPaymentIntent.
  // Display only: the regional price for this visitor. What is actually charged
  // is resolved by createPaymentIntent and arrives as finalAmount.
  const { getPrice } = useRegionalPricing();
  const baseAmount = getPrice(planId);
  const fromOnboarding = searchParams.get('fromOnboarding') === '1';
  const [pageRef, pageVisible] = useScrollAnimation();

  const amount = finalAmount !== null && !isNaN(finalAmount) && finalAmount > 0 ? finalAmount : baseAmount;
  const accessLabel = t('checkout.accessLabel', { days: PLAN_DURATION_DAYS[planId] });

  useEffect(() => {
    if (fromOnboarding && planId) {
      try { sessionStorage.setItem(CHECKOUT_FROM_ONBOARDING_KEY, planId); } catch (_) {}
    }
  }, [fromOnboarding, planId]);

  useEffect(() => {
    trackCheckoutStart({
      planId,
      amount: baseAmount,
      fromOnboarding,
      requiresAuth: !auth.currentUser,
      source: fromOnboarding ? 'onboarding' : 'pricing_page',
    });
  }, [planId, baseAmount, fromOnboarding]);

  useEffect(() => {
    const isSuccessPath = location.pathname.includes('checkout/success') || searchParams.get('payment_intent');
    if (!isSuccessPath) return;

    // Redirect-based methods (Klarna) land here whether or not the payment went
    // through; Stripe reports the outcome in `redirect_status`. A declined Klarna
    // payment used to be logged as a purchase and shown the success screen.
    // 'processing' is not a purchase yet: the webhook grants access once it settles.
    const redirectStatus = searchParams.get('redirect_status');
    if (redirectStatus !== 'succeeded' && redirectStatus !== 'processing') {
      setRedirectError(t('checkout.error.redirectFailed'));
      return;
    }

    const storedPlanId = sessionStorage.getItem(CHECKOUT_FROM_ONBOARDING_KEY);
    const paymentIntentFromUrl = searchParams.get('payment_intent') || paymentIntentId || undefined;

    if (redirectStatus === 'succeeded') {
      trackPurchase({
        transactionId: paymentIntentFromUrl,
        planId,
        planName,
        value: amount,
        source: fromOnboarding ? 'onboarding_checkout' : 'checkout',
      });
      if (typeof window.gtag === 'function')
        window.gtag('event', 'conversion', { send_to: 'AW-18013129599/jPGeCLeF6YccEP-Wqo1D', currency: 'EUR' });
    }

    try { sessionStorage.removeItem(CHECKOUT_FROM_ONBOARDING_KEY); } catch (_) {}
    setPaymentSuccess(true);
    setTimeout(() => {
      if (storedPlanId) navigate(`/onboarding?resume=1&checkout=success&planId=${storedPlanId}`);
      else navigate('/dashboard?payment=success');
    }, 2000);
  }, [location.pathname, searchParams, navigate, paymentIntentId, planId, planName, amount, fromOnboarding, t]);

  useEffect(() => {
    if (!stripePublishableKey) { setError('Stripe key not configured.'); setIsLoading(false); return; }
    if (!auth.currentUser) { navigate('/auth?mode=signup&redirect=/checkout'); return; }
    let cancelled = false;
    const init = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setVerificationError('');

        await auth.currentUser.reload();
        if (!auth.currentUser.emailVerified) {
          if (!cancelled) {
            setRequiresVerification(true);
            setClientSecret(null);
            trackVerificationGateShown({ planId });
          }
          return;
        }

        setRequiresVerification(false);
        const result = await stripeService.createPaymentIntent({
          planId, planName,
          userId: auth.currentUser.uid,
          discountCode: discountCode || null,
        });
        if (cancelled) return;
        if (result.success) {
          setClientSecret(result.clientSecret);
          setPaymentIntentId(result.paymentIntentId || null);
          setFinalAmount(typeof result.amount === 'number' ? result.amount : parseFloat(result.amount));
          setOriginalAmount(typeof result.originalAmount === 'number' ? result.originalAmount : parseFloat(result.originalAmount) || baseAmount);
          setDiscountApplied(result.discountApplied || null);
          trackPaymentIntentCreated({
            planId,
            paymentIntentId: result.paymentIntentId,
            amount: typeof result.amount === 'number' ? result.amount : parseFloat(result.amount),
            discountCode: discountCode || undefined,
          });
        } else {
          setError(result.error || t('checkout.error.initializationFailed'));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || t('checkout.error.initializationFailed'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  // baseAmount is deliberately not a dependency: it can change once the regional
  // price loads, and re-running this effect would create a second PaymentIntent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, planName, navigate, discountCode, verificationRefreshNonce]);

  const handleApplyDiscount = async () => {
    if (!discountCodeInput.trim()) { setDiscountError('Please enter a discount code'); return; }
    setIsValidatingDiscount(true); setDiscountError(null);
    try {
      const v = await stripeService.validateDiscountCode(discountCodeInput.trim());
      if (v.valid) { setDiscountCode(discountCodeInput.trim().toUpperCase()); setIsLoading(true); }
      else { setDiscountError(v.error || 'Invalid discount code'); setDiscountCode(''); setDiscountApplied(null); }
    } catch { setDiscountError('Failed to apply discount code'); setDiscountCode(''); setDiscountApplied(null); }
    finally { setIsValidatingDiscount(false); }
  };

  const handleRemoveDiscount = () => {
    setIsLoading(true);
    setDiscountCode(''); setDiscountCodeInput(''); setDiscountApplied(null);
    setDiscountError(null); setFinalAmount(null); setOriginalAmount(null);
  };

  const handleSendVerification = async () => {
    if (!auth.currentUser) return;
    setIsSendingVerification(true);
    setVerificationError('');
    try {
      const result = await sendVerificationEmail(auth.currentUser);
      if (!result.success) {
        throw new Error(result.error || t('checkout.verify.error.sendFailed', { defaultValue: 'Could not send verification email. Please try again.' }));
      }
      setVerificationSent(true);
    } catch (err) {
      setVerificationError(err.message || t('checkout.verify.error.sendFailed', { defaultValue: 'Could not send verification email. Please try again.' }));
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleRefreshVerification = async () => {
    if (!auth.currentUser) return;
    setIsRefreshingVerification(true);
    setVerificationError('');
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setVerificationSent(false);
        setRequiresVerification(false);
        setVerificationRefreshNonce((n) => n + 1);
        trackVerificationGateResolved({ planId });
      } else {
        setVerificationError(t('checkout.verify.error.notVerifiedYet', { defaultValue: 'Email is not verified yet. Check your inbox and spam folder, then try again.' }));
      }
    } catch (err) {
      setVerificationError(err.message || t('checkout.verify.error.refreshFailed', { defaultValue: 'Could not refresh verification status. Please try again.' }));
    } finally {
      setIsRefreshingVerification(false);
    }
  };

  const handlePaymentSuccess = () => {
    trackPurchase({
      transactionId: paymentIntentId || undefined,
      planId,
      planName,
      value: amount,
      source: fromOnboarding ? 'onboarding_checkout' : 'checkout',
    });

    if (typeof window.gtag === 'function')
      window.gtag('event', 'conversion', { send_to: 'AW-18013129599/jPGeCLeF6YccEP-Wqo1D', value: amount, currency: 'EUR' });
    setPaymentSuccess(true);
    setTimeout(() => {
      if (fromOnboarding) navigate(`/onboarding?resume=1&checkout=success&planId=${planId}`);
      else navigate('/dashboard?payment=success');
    }, 2000);
  };

  const stripeOptions = useMemo(() => ({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#3A86FF',
        colorBackground: '#ffffff',
        colorText: '#000000',
        colorDanger: '#ef4444',
        fontFamily: '"Space Grotesk", system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '10px',
        fontSizeBase: '15px',
        fontWeightNormal: '600',
      },
      rules: {
        '.Input': { border: '2px solid #000', boxShadow: 'none', padding: '12px 14px' },
        '.Input:focus': { border: '2px solid #3A86FF', boxShadow: '0 0 0 3px rgba(58,134,255,0.15)' },
        '.Label': { fontWeight: '800', color: '#000', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' },
        '.Tab': { border: '2px solid #000', borderRadius: '12px', fontWeight: '700' },
        '.Tab--selected': { border: '2px solid #3A86FF', backgroundColor: '#eff6ff', color: '#3A86FF' },
        '.Tab:hover': { border: '2px solid #3A86FF' },
      },
    },
  }), [clientSecret]);

  /* ── Success ── */
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center mx-auto mb-6 shadow-[8px_8px_0px_0px_rgba(58,134,255,1)]">
            <Check className="w-10 h-10 text-white stroke-[3px]" />
          </div>
          <h1 className="text-3xl font-black text-black tracking-tight mb-2">{t('checkout.success.title')}</h1>
          <p className="text-black/60 font-medium">{t('checkout.success.message')}</p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-black/40">
            <LogamiLogoSpinner size="sm" decorative={false} alt="Redirecting" />
            <span className="font-bold">Redirecting…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Order Summary block (shared) ── */
  const OrderSummary = ({ compact = false }) => (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Plan row */}
      <div className="flex items-center justify-between gap-3 p-4 bg-black text-white rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#3A86FF] flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 fill-white text-white" />
          </div>
          <div>
            <p className="font-black text-sm uppercase tracking-tight">{planName}</p>
            <p className="text-white/50 text-xs font-bold">{accessLabel}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-black text-lg">{stripeService.formatAmount(amount)}</p>
        </div>
      </div>

      {/* Discount code */}
      {!discountApplied ? (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCodeInput}
              onChange={(e) => { setDiscountCodeInput(e.target.value.toUpperCase()); setDiscountError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApplyDiscount(); }}
              placeholder="Discount code"
              className="flex-1 px-3 py-2.5 text-sm border-2 border-black/20 rounded-xl font-bold placeholder:text-black/30 placeholder:font-medium focus:outline-none focus:border-black transition-all"
            />
            <button
              onClick={handleApplyDiscount}
              disabled={isValidatingDiscount || !discountCodeInput.trim()}
              className="px-4 py-2.5 bg-black text-white text-sm rounded-xl font-black border-[2px] border-black hover:bg-[#3A86FF] hover:border-[#3A86FF] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {isValidatingDiscount ? <LogamiLogoSpinner size="sm" decorative={false} alt="Validating" /> : <Tag className="w-3.5 h-3.5" />}
              Apply
            </button>
          </div>
          {discountError && <p className="text-xs text-red-600 font-bold pl-1">{discountError}</p>}
        </div>
      ) : (
        <div className="flex items-center justify-between border-2 border-black rounded-xl px-4 py-3 bg-black/5">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-black flex items-center justify-center">
              <Check className="w-3 h-3 text-white stroke-[3px]" />
            </div>
            <div>
              <p className="text-xs font-black text-black">Discount applied</p>
              <p className="text-xs text-black/50 font-bold">{discountApplied.code}</p>
            </div>
          </div>
          <button onClick={handleRemoveDiscount} className="text-black/40 hover:text-black p-1 rounded-lg transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Total */}
      <div className="flex items-center justify-between pt-3 border-t-[2px] border-black/10">
        <span className="font-black text-black text-sm uppercase tracking-wide">Total today</span>
        <div className="text-right">
          {discountApplied && originalAmount !== null && (
            <p className="text-xs text-black/30 line-through font-bold mb-0.5">{stripeService.formatAmount(originalAmount)}</p>
          )}
          <span className="font-black text-black text-xl">{stripeService.formatAmount(amount)}</span>
        </div>
      </div>
    </div>
  );

  /* ── Payment panel ── */
  const PaymentPanel = () => (
    <>
      {requiresVerification ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 border-2 border-amber-400 bg-amber-50 rounded-2xl p-4">
            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-black text-amber-900 text-sm mb-1">
                {t('checkout.verify.title', { defaultValue: 'Verify your email before upgrading' })}
              </p>
              <p className="text-sm text-amber-800">
                {t('checkout.verify.description', { defaultValue: 'For account security, please verify your email first. After verification, you can complete payment here.' })}
              </p>
            </div>
          </div>

          {verificationSent && (
            <div className="flex items-start gap-3 border-2 border-emerald-300 bg-emerald-50 rounded-2xl p-4">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800 font-semibold">
                {t('checkout.verify.sent', { defaultValue: 'Verification email sent. Check your inbox and spam folder.' })}
              </p>
            </div>
          )}

          {verificationError && (
            <div className="flex items-start gap-3 border-2 border-red-300 bg-red-50 rounded-2xl p-4">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-semibold">{verificationError}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleSendVerification}
            disabled={isSendingVerification}
            className="w-full flex items-center justify-center gap-2 py-3 border-[2px] border-black rounded-xl text-sm font-black text-black hover:bg-black hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSendingVerification ? <LogamiLogoSpinner size="sm" decorative={false} alt="Sending" /> : <Mail className="w-4 h-4" />}
            {isSendingVerification
              ? t('checkout.verify.sending', { defaultValue: 'Sending verification email...' })
              : t('checkout.verify.sendButton', { defaultValue: 'Send verification email' })}
          </button>

          <button
            type="button"
            onClick={handleRefreshVerification}
            disabled={isRefreshingVerification}
            className="w-full flex items-center justify-center gap-2 py-3 bg-black text-white rounded-xl text-sm font-black border-[2px] border-black hover:bg-[#3A86FF] hover:border-[#3A86FF] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isRefreshingVerification ? <LogamiLogoSpinner size="sm" decorative={false} alt="Checking" /> : <RefreshCw className="w-4 h-4" />}
            {isRefreshingVerification
              ? t('checkout.verify.checking', { defaultValue: 'Checking verification...' })
              : t('checkout.verify.refreshButton', { defaultValue: "I've verified, continue" })}
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <LogamiLogoSpinner size="lg" decorative={false} alt="Loading" />
          <p className="text-sm text-black/40 font-bold">{t('checkout.loading')}</p>
        </div>
      ) : error ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 border-2 border-red-400 bg-red-50 rounded-2xl p-4">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-black text-red-800 text-sm mb-0.5">{t('checkout.error.title')}</p>
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full flex items-center justify-center gap-2 py-3 border-[2px] border-black rounded-xl text-sm font-black text-black hover:bg-black hover:text-white transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            {t('checkout.error.retry')}
          </button>
        </div>
      ) : clientSecret && stripePromise ? (
        <>
        {redirectError && (
          <div role="alert" className="flex items-start gap-3 border-2 border-red-400 bg-red-50 rounded-2xl p-4 mb-5">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-black text-red-800 text-sm mb-0.5">{t('checkout.error.title')}</p>
              <p className="text-red-600 text-sm font-medium">{redirectError}</p>
            </div>
          </div>
        )}
        <Elements stripe={stripePromise} options={stripeOptions} key={clientSecret}>
          <CheckoutForm
            planId={planId}
            planName={planName}
            amount={amount}
            clientSecret={clientSecret}
            onSuccess={handlePaymentSuccess}
            onError={(err) => setError(err.message)}
          />
        </Elements>
        </>
      ) : null}
    </>
  );

  return (
    <div className="min-h-screen bg-white font-space">
      {/* ── Mobile sticky top bar ── */}
      <div className="lg:hidden sticky top-0 z-20 bg-white border-b-[2px] border-black/10 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate(fromOnboarding ? '/onboarding?resume=1&checkout=skipped' : '/pricing')}
          className="flex items-center gap-1.5 text-black/60 font-black text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <span className="text-xs font-black text-black/50 uppercase tracking-wide">Secure</span>
        </div>
        <span className="font-black text-black text-base">{stripeService.formatAmount(amount)}</span>
      </div>

      <div
        ref={pageRef}
        className={`max-w-5xl mx-auto px-4 py-6 lg:py-12 transition-all duration-700 ${pageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Desktop top bar */}
        <div className="hidden lg:flex items-center justify-between mb-10">
          <button
            onClick={() => navigate(fromOnboarding ? '/onboarding?resume=1&checkout=skipped' : '/pricing')}
            className="flex items-center gap-2 text-black/50 hover:text-black font-black text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {fromOnboarding ? 'Back to setup' : t('checkout.backToPricing')}
          </button>
          <div className="flex items-center gap-2 text-sm font-black text-black/40">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            Secured by Stripe
          </div>
        </div>

        {/* ── MOBILE: payment card first, then summary ── */}
        <div className="lg:hidden space-y-4">
          {/* Payment */}
          <div className="bg-white border-[3px] border-black rounded-3xl p-5 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-black text-black text-xl uppercase tracking-tight">Pay now</h2>
              <div className="flex items-center gap-1 text-xs font-black text-black/40">
                <Lock className="w-3 h-3" />SSL
              </div>
            </div>
            <PaymentPanel />
          </div>

          {/* Order summary */}
          <div className="bg-white border-[3px] border-black rounded-3xl p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.08)]">
            <h3 className="font-black text-black text-xs uppercase tracking-widest mb-4 opacity-50">Order summary</h3>
            <OrderSummary compact />
          </div>

          {fromOnboarding && (
            <p className="text-xs text-center text-black/30 font-bold pb-4">
              Want to start free?{' '}
              <button type="button" onClick={() => navigate('/onboarding?resume=1&checkout=skipped')} className="underline text-black/60 hover:text-black">
                Continue with free plan
              </button>
            </p>
          )}
        </div>

        {/* ── DESKTOP: two columns ── */}
        <div className="hidden lg:grid lg:grid-cols-[1fr_420px] gap-8 items-start">

          {/* Left column */}
          <div className="space-y-6">
            {/* Order summary card */}
            <div className="bg-white border-[3px] border-black rounded-3xl p-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="font-black text-black text-2xl uppercase tracking-tight mb-6">Order summary</h2>
              <OrderSummary />
            </div>

            {/* What's included */}
            <div className="bg-black text-white border-[3px] border-black rounded-3xl p-8 shadow-[12px_12px_0px_0px_rgba(58,134,255,1)]">
              <h3 className="font-black text-xs uppercase tracking-widest text-white/50 mb-5">What you get</h3>
              <ul className="space-y-3">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-md bg-[#3A86FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white stroke-[3px]" />
                    </div>
                    <span className="text-sm font-bold text-white/80">{f}</span>
                  </li>
                ))}
              </ul>
              {fromOnboarding && (
                <p className="mt-6 text-xs text-white/30 border-t border-white/10 pt-4 font-bold">
                  Want to start free instead?{' '}
                  <button type="button" onClick={() => navigate('/onboarding?resume=1&checkout=skipped')} className="underline text-white/60 hover:text-white">
                    Continue with free plan
                  </button>
                </p>
              )}
            </div>
          </div>

          {/* Right column, sticky payment */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white border-[3px] border-black rounded-3xl p-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-black text-black text-2xl uppercase tracking-tight">Payment</h2>
                <div className="flex items-center gap-1.5 text-xs font-black text-black/40">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                  SSL encrypted
                </div>
              </div>
              <PaymentPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
